import { Writer } from '@transcend-io/conflux';
import streamSaver from 'streamsaver';
import { ReadableStream as ReadableStreamPonyfill } from 'web-streams-polyfill/ponyfill';

interface ZipItem {
  name: string;
  url: string;
}

function createZipFromItems(
  items: Iterable<ZipItem>,
  zipName: string,
  onDone?: () => void
): void {
  const ModernReadableStream = window.WritableStream
    ? window.ReadableStream
    : ReadableStreamPonyfill;

  const iterator = items[Symbol.iterator]();

  new (ModernReadableStream as typeof window.ReadableStream)({
    async pull(ctrl) {
      const { done, value } = iterator.next();

      if (done) {
        onDone?.();
        return ctrl.close();
      }

      const { name, url } = value;
      const response = await fetch(url);

      return ctrl.enqueue({
        name,
        stream: () => response.body,
      });
    },
  })
    .pipeThrough(new Writer())
    .pipeTo(streamSaver.createWriteStream(`${zipName}.zip`));
}

function parseFileNameFromSignedUrl(signedUrl: string): string {
  return decodeURIComponent(decodeURIComponent(signedUrl))
    .split('filename=')[1]
    .split('"&')[0]
    .replace('"', '');
}

function generateReportName(firstSignedUrl: string): string {
  const firstFileName = parseFileNameFromSignedUrl(firstSignedUrl);
  const reportNameArray = firstFileName.split('_');
  reportNameArray.pop();
  return reportNameArray.join('_');
}

export function downloadZip(
  signedRequest: string[],
  onDone: () => void
): void {
  const reportName = generateReportName(signedRequest[0]);

  function* mapSignedUrlsToItems(): Iterable<ZipItem> {
    for (const signedUrl of signedRequest) {
      yield {
        name: parseFileNameFromSignedUrl(signedUrl),
        url: signedUrl,
      };
    }
  }

  createZipFromItems(mapSignedUrlsToItems(), reportName, onDone);
}

export function downloadZipFromDirectory(
  items: { name: string; url: string }[],
  zipName: string,
  onDone?: () => void
): void {
  const uniqueItems = makeUniqueNames(items);
  createZipFromItems(uniqueItems, zipName, onDone);
}

function makeUniqueNames(items: ZipItem[]): ZipItem[] {
  const seen = new Set<string>();

  return items.map((item) => {
    if (!seen.has(item.name)) {
      seen.add(item.name);
      return item;
    }

    let counter = 1;
    let newName: string;
    do {
      const dotIndex = item.name.lastIndexOf('.');
      newName =
        dotIndex > 0
          ? `${item.name.slice(0, dotIndex)} (${counter})${item.name.slice(dotIndex)}`
          : `${item.name} (${counter})`;
      counter++;
    } while (seen.has(newName));

    seen.add(newName);
    return { ...item, name: newName };
  });
}
